import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "wfw_currency_v1";

// Display-only currency conversion. Stripe charges always settle in USD.
// Rates fetched from backend (cached 6h server-side).

const CurrencyContext = createContext(null);

const SYMBOLS = {
  USD: "$", EUR: "€", GBP: "£", MXN: "MX$",
  CAD: "C$", BRL: "R$", COP: "COL$", AUD: "A$",
};

const CURRENCY_LABELS = {
  USD: "US Dollar", EUR: "Euro", GBP: "British Pound", MXN: "Mexican Peso",
  CAD: "Canadian Dollar", BRL: "Brazilian Real", COP: "Colombian Peso", AUD: "Australian Dollar",
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrencyState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || "USD"; } catch { return "USD"; }
  });
  const [rates, setRates] = useState({ USD: 1.0 });
  const [supported, setSupported] = useState(["USD"]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/currency/rates?base=USD`);
        setRates(data.rates || { USD: 1.0 });
        setSupported(data.supported || ["USD"]);
      } catch {
        setRates({ USD: 1.0 });
        setSupported(["USD"]);
      } finally {
        setReady(true);
      }
    };
    load();
  }, []);

  const setCurrency = useCallback((next) => {
    setCurrencyState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
  }, []);

  const convert = useCallback((usdValue) => {
    const v = Number(usdValue);
    if (!Number.isFinite(v)) return 0;
    const rate = rates[currency] || 1.0;
    return v * rate;
  }, [currency, rates]);

  const formatPrice = useCallback((usdValue, { compact = false } = {}) => {
    const converted = convert(usdValue);
    const symbol = SYMBOLS[currency] || `${currency} `;
    // Round differently for currencies with large magnitudes
    const decimals = currency === "COP" || (converted >= 10000 && compact) ? 0 : 2;
    const formatted = converted.toLocaleString(undefined, {
      minimumFractionDigits: decimals, maximumFractionDigits: decimals,
    });
    return `${symbol}${formatted}`;
  }, [currency, convert]);

  return (
    <CurrencyContext.Provider value={{
      currency, setCurrency, rates, supported, ready,
      convert, formatPrice, symbol: SYMBOLS[currency] || currency,
      label: CURRENCY_LABELS[currency] || currency,
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  // Safe fallback for components rendered before provider (shouldn't happen)
  return ctx || {
    currency: "USD",
    setCurrency: () => {},
    rates: { USD: 1.0 },
    supported: ["USD"],
    ready: true,
    convert: (v) => Number(v) || 0,
    formatPrice: (v) => `$${(Number(v) || 0).toFixed(2)}`,
    symbol: "$",
    label: "US Dollar",
  };
};

export { SYMBOLS, CURRENCY_LABELS };
